/**
 * Regras de realização de atendimento / triagem (Central Amparo).
 * A tolerância após o scheduled_at vem do parâmetro agendamento_tolerancia_minutos.
 */

import { DEFAULT_SCHEDULING_LIMITS } from '../types/supportCenterParameters'

/**
 * false = atendimento só no horário agendado, com a tolerância do parâmetro.
 * true libera iniciar e concluir fora do horário. A triagem não usa este interruptor.
 */
export const ATTENDANCE_TIME_WINDOW_DISABLED = false

export function attendancePastMessage(toleranceMinutes: number): string {
  const m = Math.max(0, toleranceMinutes)
  return `Não é possível realizar atendimento de agendamento com data/hora já passada (tolerância de ${m} minuto${m === 1 ? '' : 's'}).`
}

export function attendanceEarlyMessage(): string {
  return 'O atendimento só fica disponível a partir do horário agendado.'
}

export function attendanceFormPastMessage(toleranceMinutes: number): string {
  const m = Math.max(0, toleranceMinutes)
  return `Não é possível abrir o formulário de atendimento de agendamento com data/hora já passada (tolerância de ${m} minuto${m === 1 ? '' : 's'}).`
}

export function attendanceFormEarlyMessage(): string {
  return 'O formulário de atendimento só fica disponível a partir do horário agendado.'
}

export function triagePastMessage(toleranceMinutes: number): string {
  const m = Math.max(0, toleranceMinutes)
  return `Não é possível realizar triagem de agendamento com data/hora já passada (tolerância de ${m} minuto${m === 1 ? '' : 's'}).`
}

function parseScheduledAt(value: string): Date | null {
  if (!value) return null
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

/** true se ainda não chegou o horário agendado. */
export function isAppointmentBeforeScheduled(
  scheduledAt: string,
  now: Date = new Date()
): boolean {
  const when = parseScheduledAt(scheduledAt)
  if (!when) return true
  return now.getTime() < when.getTime()
}

/** true se já passou o horário + tolerância em minutos. */
export function isAppointmentPastForAttendance(
  scheduledAt: string,
  toleranceMinutes: number = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  now: Date = new Date()
): boolean {
  const when = parseScheduledAt(scheduledAt)
  if (!when) return true
  const toleranceMs = Math.max(0, toleranceMinutes) * 60 * 1000
  return now.getTime() > when.getTime() + toleranceMs
}

type AttendanceEventRef = {
  status: string
  triage_status?: string | null
  scheduled_at: string
}

export function canPerformAttendance(
  event: AttendanceEventRef | null | undefined,
  canUpdate: boolean,
  toleranceMinutes: number = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  now: Date = new Date()
): boolean {
  if (!canUpdate || !event) return false
  if (event.status !== 'marcado' && event.status !== 'reagendado') return false
  if (event.triage_status !== 'concluida') return false
  if (ATTENDANCE_TIME_WINDOW_DISABLED) return true
  if (isAppointmentBeforeScheduled(event.scheduled_at, now)) return false
  if (isAppointmentPastForAttendance(event.scheduled_at, toleranceMinutes, now)) return false
  return true
}

type TriageEditableRef = {
  status: string
  appointment?: { scheduled_at?: string | null } | null
}

/** Triagem só é editável enquanto pendente e dentro da janela do agendamento. */
export function canPerformTriage(
  triage: TriageEditableRef | null | undefined,
  canUpdate: boolean,
  toleranceMinutes: number = DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes,
  now: Date = new Date()
): boolean {
  if (!canUpdate || !triage) return false
  if (triage.status !== 'pendente') return false
  const scheduledAt = triage.appointment?.scheduled_at
  if (scheduledAt && isAppointmentPastForAttendance(scheduledAt, toleranceMinutes, now)) {
    return false
  }
  return true
}
